import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ApiIcon from '@mui/icons-material/Api';
import ApprovalIcon from '@mui/icons-material/Approval';
import BusinessIcon from '@mui/icons-material/Business';
import DatasetIcon from '@mui/icons-material/Dataset';
import GroupsIcon from '@mui/icons-material/Groups';
import InventoryIcon from '@mui/icons-material/Inventory';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TimelineIcon from '@mui/icons-material/Timeline';
import type { SvgIconComponent } from '@mui/icons-material';

import type { ErpModule } from '@/api/platform.api';
import { FEATURE } from '@/core/features/featureCodes';
import {
  LAUNCHER_ERP_MODULES,
  moduleAdminPath,
  moduleAdminRoutes,
  moduleSlug,
  type ModuleAdminPage,
} from '@/config/moduleDefaults';
import {
  HR_MASTER_DATA_SETTINGS_ENTRIES,
  resolveHrMasterDataLegacySettingsPath,
  type HrMasterDataSettingsEntry,
} from '@/config/hrMasterDataSettings';
import { access, resourceAccess, type RouteAccessRule } from '@/config/routeAccessUtils';

/** Module-scoped administration URLs (all workspaces) for settings legacy redirects. */
function moduleScopedAdminLegacyPaths(...pages: ModuleAdminPage[]): string[] {
  return LAUNCHER_ERP_MODULES.flatMap((mod) => pages.map((page) => moduleAdminPath(mod, page)));
}

export type SettingsCategoryKey =
  | 'organization'
  | 'hr-master-data'
  | 'hr'
  | 'finance'
  | 'inventory'
  | 'crm'
  | 'marketing'
  | 'projects'
  | 'help-desk'
  | 'communication'
  | 'workflow'
  | 'approvals'
  | 'notifications'
  | 'developer';

export type SettingsComponentKey =
  | 'organizationProfile'
  | 'companies'
  | 'employees'
  | 'roles'
  | 'stores'
  | 'products'
  | 'categories'
  | 'brands'
  | 'units'
  | 'taxRules'
  | 'storeProducts'
  | 'discounts'
  | 'inventory'
  | 'inventoryLedger'
  | 'warehouseSetup'
  | 'hrSetup'
  | 'hrAllowanceTypes'
  | 'hrMasterDataSettings'
  | 'hrOrg'
  | 'hrAdmin'
  | 'financeSetup'
  | 'financeTaxes'
  | 'financeRules'
  | 'financeAudit'
  | 'financeWorkflows'
  | 'financeLedger'
  | 'financeControl'
  | 'financeHealth'
  | 'financeClose'
  | 'financeAutomation'
  | 'financeIntegrations'
  | 'financeAccounting'
  | 'crmSetup'
  | 'crmContacts'
  | 'crmCompanies'
  | 'customers'
  | 'leadGenSources'
  | 'marketingTemplates'
  | 'marketingSegments'
  | 'marketingSuppression'
  | 'marketingWebhooks'
  | 'marketingAcquisition'
  | 'projectsSetup'
  | 'projectsBoards'
  | 'projectsIssues'
  | 'projectsResources'
  | 'projectsAutomation'
  | 'deskSetup'
  | 'deskWorkflow'
  | 'deskCannedResponses'
  | 'deskKnowledgeBase'
  | 'deskBoards'
  | 'approvalsBuilder'
  | 'approvalsMatrix'
  | 'approvalDelegations'
  | 'notificationsCenter'
  | 'socialConnect'
  | 'socialSettings'
  | 'socialWebhooks'
  | 'translations'
  | 'moduleSubscription'
  | 'genericMasterPlaceholder';

export type SettingsCategory = {
  key: SettingsCategoryKey;
  label: string;
  description: string;
  icon: SvgIconComponent;
  order: number;
};

export type SettingsMaster = {
  key: string;
  label: string;
  description: string;
  category: SettingsCategoryKey;
  route: string;
  legacyRoutes: string[];
  componentKey: SettingsComponentKey;
  access: RouteAccessRule;
  feature?: string;
  tags?: string[];
  quickAction?: string;
  /** ERP module used for role screen grouping and module-access filtering. */
  moduleKey?: SettingsModuleKey;
};

/** Module bucket for settings routes in RBAC and Settings UI grouping. */
export type SettingsModuleKey = ErpModule | 'PLATFORM';

export const SETTINGS_PATH = '/settings';

export const SETTINGS_CATEGORIES: SettingsCategory[] = [
  { key: 'organization', label: 'Organization', description: 'Companies, stores, users, roles, subscription, languages and business structure.', icon: BusinessIcon, order: 10 },
  { key: 'hr-master-data', label: 'HR Master Data', description: 'Leave types, departments, holidays, shift schedules, allowance types and HR reference data.', icon: DatasetIcon, order: 25 },
  { key: 'hr', label: 'Human Resources', description: 'HR setup, reporting hierarchy and workforce administration.', icon: GroupsIcon, order: 30 },
  { key: 'finance', label: 'Finance', description: 'Finance setup, taxes, ledger, rules and audit configuration.', icon: AccountBalanceIcon, order: 40 },
  { key: 'inventory', label: 'Inventory & POS', description: 'Products, catalog, stores, warehouses, stock and pricing setup.', icon: InventoryIcon, order: 50 },
  { key: 'crm', label: 'Sales & CRM', description: 'CRM setup, customers, lead sources and sales reference data.', icon: PeopleIcon, order: 60 },
  { key: 'marketing', label: 'Marketing', description: 'Templates, audiences, consent and marketing automation setup.', icon: TimelineIcon, order: 70 },
  { key: 'projects', label: 'Projects', description: 'Project boards, issues, resources and automation configuration.', icon: AccountTreeIcon, order: 80 },
  { key: 'help-desk', label: 'Help Desk', description: 'Desk workflows, canned responses, knowledge base and ticket setup.', icon: SupportAgentIcon, order: 90 },
  { key: 'communication', label: 'Communication', description: 'Channels, templates and social communication setup.', icon: NotificationsActiveIcon, order: 100 },
  { key: 'workflow', label: 'Workflow', description: 'Workflow builders, automations and business processes.', icon: AccountTreeIcon, order: 110 },
  { key: 'approvals', label: 'Approvals', description: 'Approval builder, matrix and delegation settings.', icon: ApprovalIcon, order: 120 },
  { key: 'notifications', label: 'Notifications', description: 'Notification center and delivery preferences.', icon: NotificationsActiveIcon, order: 130 },
  { key: 'developer', label: 'Developer', description: 'API keys, webhooks and integration settings.', icon: ApiIcon, order: 160 },
];

/** Default ERP module for a settings category (individual masters may override). */
export const SETTINGS_CATEGORY_MODULE: Record<SettingsCategoryKey, SettingsModuleKey> = {
  organization: 'PLATFORM',
  'hr-master-data': 'HR',
  hr: 'HR',
  finance: 'FINANCE',
  inventory: 'POS',
  crm: 'CRM',
  marketing: 'MARKETING',
  projects: 'PROJECTS',
  'help-desk': 'DESK',
  communication: 'SOCIAL',
  workflow: 'PLATFORM',
  approvals: 'PLATFORM',
  notifications: 'PLATFORM',
  developer: 'PLATFORM',
};

export const SETTINGS_MODULE_GROUP_LABELS: Record<SettingsModuleKey, string> = {
  PLATFORM: 'Platform & organization',
  POS: 'Point of Sale',
  INVENTORY: 'Inventory',
  CRM: 'CRM',
  HR: 'HRMS',
  FINANCE: 'Finance',
  PROJECTS: 'Projects',
  DESK: 'Help desk',
  MARKETING: 'Marketing',
  SOCIAL: 'Social',
  PROCUREMENT: 'Procurement',
  WAREHOUSE: 'Warehouse',
  LEAD_GEN: 'Lead Generation',
  LOYALTY: 'Loyalty',
  REPORTING: 'Reporting',
  ANALYTICS: 'Analytics',
  PAYROLL: 'Payroll',
  DELIVERY: 'Delivery',
  ECOMMERCE: 'E-commerce',
};

export const SETTINGS_MODULE_GROUP_ORDER: SettingsModuleKey[] = [
  'PLATFORM',
  'POS',
  'CRM',
  'HR',
  'FINANCE',
  'PROJECTS',
  'DESK',
  'MARKETING',
  'SOCIAL',
  'PROCUREMENT',
  'INVENTORY',
  'WAREHOUSE',
  'LEAD_GEN',
];

const settingsPath = (category: SettingsCategoryKey, key: string) => `${SETTINGS_PATH}/${category}/${key}`;

function master(
  category: SettingsCategoryKey,
  key: string,
  label: string,
  description: string,
  componentKey: SettingsComponentKey,
  accessRule: RouteAccessRule,
  legacyRoutes: string[],
  options: Pick<SettingsMaster, 'feature' | 'tags' | 'quickAction' | 'moduleKey'> = {},
): SettingsMaster {
  const { moduleKey: explicitModuleKey, ...rest } = options;
  return {
    key,
    label,
    description,
    category,
    route: settingsPath(category, key),
    legacyRoutes,
    componentKey,
    access: { ...accessRule, feature: options.feature ?? accessRule.feature },
    moduleKey: explicitModuleKey ?? SETTINGS_CATEGORY_MODULE[category],
    ...rest,
  };
}

function hrMasterDataSettingsMaster(entry: HrMasterDataSettingsEntry): SettingsMaster {
  const accessRule: RouteAccessRule =
    entry.kind === 'devices-page'
      ? access(['HR_VIEW', 'HR_ATTENDANCE_MANAGE'], 'HR_ATTENDANCE_MANAGE', FEATURE.HR_ATTENDANCE)
      : entry.kind === 'holiday-tab'
        ? access(['HR_VIEW', 'HR_LEAVE_MANAGE'], 'HR_MANAGE', FEATURE.HR_BASIC)
        : access(
            ['HR_VIEW', 'HR_LEAVE_MANAGE', 'HR_LEAVE_POLICY_MANAGE', 'HR_MANAGE'],
            'HR_MANAGE',
            FEATURE.HR_BASIC,
          );
  const feature = entry.kind === 'devices-page' ? FEATURE.HR_ATTENDANCE : FEATURE.HR_BASIC;
  return master(
    'hr-master-data',
    entry.key,
    entry.label,
    entry.description,
    'hrMasterDataSettings',
    accessRule,
    entry.legacyRoutes,
    { feature },
  );
}

export const SETTINGS_MASTERS: SettingsMaster[] = [
  master('organization', 'organization-profile', 'Organization Profile', 'Tenant profile, branding and organization defaults.', 'organizationProfile', access(['ORG_VIEW', 'ORG_MANAGE']), [...moduleScopedAdminLegacyPaths('organization'), '/org/profile', '/settings/system/system-parameters']),
  master('organization', 'companies', 'Companies', 'Legal entities and company scope configuration.', 'companies', access(['COMPANY_VIEW', 'ORG_MANAGE'], 'COMPANY_MANAGE'), moduleScopedAdminLegacyPaths('companies')),
  master('organization', 'users', 'Users', 'Workspace users and employee-linked account access.', 'employees', access(['EMPLOYEE_VIEW', 'ORG_MANAGE'], 'EMPLOYEE_MANAGE', FEATURE.EMPLOYEE_BASIC), [...moduleScopedAdminLegacyPaths('users'), '/org/employees'], { feature: FEATURE.EMPLOYEE_BASIC }),
  master('organization', 'roles', 'Roles & Screen Access', 'Roles, permissions and screen action matrix.', 'roles', resourceAccess('ROLE_VIEW', { create: 'ROLE_MANAGE', edit: 'ROLE_MANAGE', delete: 'ROLE_MANAGE' }, FEATURE.EMPLOYEE_BASIC), [...moduleScopedAdminLegacyPaths('roles'), '/org/roles', '/settings/security/roles', '/settings/security/permissions', '/settings/organization/permissions'], { feature: FEATURE.EMPLOYEE_BASIC }),
  master('organization', 'subscription', 'Subscription', 'Subscription, plans and entitlement settings.', 'moduleSubscription', access(['SUBSCRIPTION_MANAGE', 'ORG_MANAGE']), [...moduleScopedAdminLegacyPaths('subscription'), '/org/subscription', '/org/administration/billing', '/org/workspace/subscription', '/settings/system/subscription']),
  master('organization', 'translations', 'Translations', 'Translation keys, language resources, import/export and localization diagnostics.', 'translations', access('LOCALIZATION_MANAGE'), ['/settings/localization/translations', '/settings/localization'], { tags: ['i18n', 'language', 'translations'] }),
  master('organization', 'stores', 'Stores', 'Store and POS location master data.', 'stores', access('STORE_VIEW', 'STORE_MANAGE'), ['/pos/stores'], { moduleKey: 'POS' }),

  master('hr', 'hr-setup', 'HR Setup', 'General HR configuration and setup checklist.', 'hrSetup', access('HR_VIEW', 'HR_MANAGE', FEATURE.HR_BASIC), ['/hr/setup'], { feature: FEATURE.HR_BASIC }),
  ...HR_MASTER_DATA_SETTINGS_ENTRIES.map(hrMasterDataSettingsMaster),
  master('hr-master-data', 'allowance-types', 'Allowance Types', 'Configurable allowance policies for payroll, claims and expenses.', 'hrAllowanceTypes', access(['HR_VIEW', 'HR_ALLOWANCE_VIEW'], 'HR_ALLOWANCE_MANAGE', FEATURE.HR_BASIC), ['/hr/allowances', '/settings/compensation-benefits/allowance-types'], { feature: FEATURE.HR_BASIC, tags: ['allowance', 'payroll', 'claims', 'expenses'] }),
  master('hr', 'org-structure', 'Reporting Hierarchy', 'Departments, designations and reporting relationships.', 'hrOrg', access(['REPORTING_RELATIONSHIP_VIEW', 'HR_VIEW'], ['REPORTING_RELATIONSHIP_EDIT', 'HR_MANAGE'], FEATURE.HR_BASIC), ['/hr/org'], { feature: FEATURE.HR_BASIC }),
  master('hr', 'hr-admin', 'HR Administration', 'HR administrator settings and device administration.', 'hrAdmin', access('HR_VIEW', 'HR_ADMIN', FEATURE.HR_BASIC), ['/hr/admin'], { feature: FEATURE.HR_BASIC }),

  master('finance', 'finance-control', 'Administration Home', 'Finance control center, close, health and platform administration.', 'financeControl', access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BASIC), ['/finance/control'], { feature: FEATURE.FINANCE_BASIC }),
  master('finance', 'health-center', 'Health Center', 'Finance data health checks and remediation.', 'financeHealth', access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BASIC), ['/finance/health'], { feature: FEATURE.FINANCE_BASIC }),
  master('finance', 'period-close', 'Period Close', 'Accounting period close and lock controls.', 'financeClose', access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/close'], { feature: FEATURE.FINANCE_ACCOUNTING }),
  master('finance', 'automation-center', 'Automation Center', 'Finance automation rules and scheduled jobs.', 'financeAutomation', access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/automation'], { feature: FEATURE.FINANCE_ACCOUNTING }),
  master('finance', 'integration-health', 'Integration Health', 'Finance integration status and delivery monitoring.', 'financeIntegrations', access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_BASIC), ['/finance/integrations'], { feature: FEATURE.FINANCE_BASIC }),
  master('finance', 'accounting-advanced', 'Accounting (Advanced)', 'Advanced accounting configuration and controls.', 'financeAccounting', access('FINANCE_VIEW', 'FINANCE_JOURNAL_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/accounting'], { feature: FEATURE.FINANCE_ACCOUNTING }),
  master('finance', 'finance-setup', 'Finance Setup', 'Finance configuration, fiscal settings and setup workflows.', 'financeSetup', access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_BASIC), ['/finance/setup', '/finance/setup/wizard'], { feature: FEATURE.FINANCE_BASIC }),
  master('finance', 'taxes', 'Tax Codes', 'Tax code, tax group and compliance setup.', 'financeTaxes', access('FINANCE_VIEW', 'FINANCE_TAX_MANAGE', FEATURE.FINANCE_BASIC), ['/finance/taxes'], { feature: FEATURE.FINANCE_BASIC }),
  master('finance', 'business-rules', 'Business Rules', 'Finance control rules and accounting validations.', 'financeRules', access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/control/rules'], { feature: FEATURE.FINANCE_ACCOUNTING }),
  master('finance', 'ledger', 'General Ledger', 'Chart of accounts and ledger configuration.', 'financeLedger', access('FINANCE_VIEW', 'FINANCE_JOURNAL_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/ledger'], { feature: FEATURE.FINANCE_ACCOUNTING }),
  master('finance', 'audit', 'Finance Audit Trail', 'Finance audit history and change traceability.', 'financeAudit', access('FINANCE_VIEW', 'FINANCE_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/audit', '/settings/system/audit-logs'], { feature: FEATURE.FINANCE_ACCOUNTING }),
  master('finance', 'workflows', 'Finance Workflows', 'Finance workflow configuration and designer.', 'financeWorkflows', access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/workflows'], { feature: FEATURE.FINANCE_ACCOUNTING }),

  master('inventory', 'products', 'Products', 'Product catalog master data.', 'products', access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING), ['/pos/products'], { feature: FEATURE.POS_BILLING }),
  master('inventory', 'categories', 'Categories', 'Product category hierarchy.', 'categories', access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING), ['/pos/categories'], { feature: FEATURE.POS_BILLING }),
  master('inventory', 'brands', 'Brands', 'Product brand master data.', 'brands', access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING), ['/pos/brands'], { feature: FEATURE.POS_BILLING }),
  master('inventory', 'units', 'Units', 'Units of measure and product quantity setup.', 'units', access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING), ['/pos/units'], { feature: FEATURE.POS_BILLING }),
  master('inventory', 'tax-rules', 'Tax Rules', 'POS tax rules and product tax setup.', 'taxRules', access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING), ['/pos/tax-rules', '/pos/taxes'], { feature: FEATURE.POS_BILLING }),
  master('inventory', 'store-products', 'Store Products', 'Store-wise product catalog assignment.', 'storeProducts', access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING), ['/pos/store-products'], { feature: FEATURE.POS_BILLING }),
  master('inventory', 'discounts', 'Discounts', 'Discount and promotion master data.', 'discounts', access('PRODUCT_VIEW', 'PRODUCT_MANAGE', FEATURE.POS_BILLING), ['/pos/discounts'], { feature: FEATURE.POS_BILLING }),
  master('inventory', 'stock', 'Stock Settings', 'Inventory stock and adjustment settings.', 'inventory', access('INVENTORY_VIEW', 'INVENTORY_MANAGE', FEATURE.INVENTORY), ['/pos/inventory'], { feature: FEATURE.INVENTORY, moduleKey: 'INVENTORY' }),
  master('inventory', 'stock-ledger', 'Stock Ledger', 'Inventory ledger and stock movement audit.', 'inventoryLedger', access('INVENTORY_VIEW', 'INVENTORY_MANAGE', FEATURE.INVENTORY), ['/pos/inventory/ledger'], { feature: FEATURE.INVENTORY, moduleKey: 'INVENTORY' }),
  master('inventory', 'warehouses', 'Warehouses', 'Warehouse, zone and bin configuration.', 'warehouseSetup', access('WAREHOUSE_VIEW', 'WAREHOUSE_ADMIN', FEATURE.WAREHOUSE), ['/warehouse/setup'], { feature: FEATURE.WAREHOUSE, moduleKey: 'WAREHOUSE' }),

  master('crm', 'crm-setup', 'CRM Setup', 'Lead statuses, sources, pipelines and CRM configuration.', 'crmSetup', access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC), ['/crm/setup'], { feature: FEATURE.CRM_BASIC }),
  master('crm', 'lead-sources', 'Lead Sources', 'Lead source configuration for acquisition and CRM handoff.', 'leadGenSources', access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'), ['/lead-gen/sources'], { moduleKey: 'LEAD_GEN' }),
  master('crm', 'contacts', 'Contacts', 'CRM contact master data.', 'crmContacts', access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC), ['/crm/contacts'], { feature: FEATURE.CRM_BASIC }),
  master('crm', 'crm-companies', 'CRM Companies', 'CRM account and company records.', 'crmCompanies', access('CRM_VIEW', 'CRM_MANAGE', FEATURE.CRM_BASIC), ['/crm/companies'], { feature: FEATURE.CRM_BASIC }),
  master('crm', 'customers', 'Customers', 'Shared customer master data.', 'customers', access('CUSTOMER_VIEW', 'CUSTOMER_MANAGE', FEATURE.CRM_BASIC), ['/pos/customers', '/crm/customers'], { feature: FEATURE.CRM_BASIC }),

  master('marketing', 'templates', 'Marketing Templates', 'Email, WhatsApp and campaign templates.', 'marketingTemplates', access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_EMAIL), ['/marketing/templates'], { feature: FEATURE.MARKETING_EMAIL }),
  master('marketing', 'segments', 'Audience Segments', 'Marketing segments and target audiences.', 'marketingSegments', access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_BASIC), ['/marketing/segments'], { feature: FEATURE.MARKETING_BASIC }),
  master('marketing', 'suppression', 'Consent & Suppression', 'Consent, suppression lists and channel compliance.', 'marketingSuppression', access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_EMAIL), ['/marketing/suppression'], { feature: FEATURE.MARKETING_EMAIL }),
  master('marketing', 'webhooks', 'Marketing Webhooks', 'ESP webhook and integration settings.', 'marketingWebhooks', access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_EMAIL), ['/marketing/webhooks'], { feature: FEATURE.MARKETING_EMAIL }),
  master('marketing', 'acquisition', 'Forms & Landing Pages', 'Acquisition builder and public lead capture configuration.', 'marketingAcquisition', access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.LEAD_GEN_BASIC), ['/marketing/acquisition'], { feature: FEATURE.LEAD_GEN_BASIC }),

  master('projects', 'project-setup', 'Project Setup', 'Project type, task, milestone and planning setup.', 'projectsSetup', access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC), ['/projects/setup'], { feature: FEATURE.PROJECTS_BASIC }),
  master('projects', 'boards', 'Boards', 'Project boards and workflow states.', 'projectsBoards', access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC), ['/projects/boards'], { feature: FEATURE.PROJECTS_BASIC }),
  master('projects', 'issues', 'Issues', 'Issue types, status and defect workflow setup.', 'projectsIssues', access('PROJECT_VIEW', 'PROJECT_ISSUE_MANAGE', FEATURE.PROJECTS_BASIC), ['/projects/issues'], { feature: FEATURE.PROJECTS_BASIC }),
  master('projects', 'resources', 'Resources', 'Project resources and capacity settings.', 'projectsResources', access('PROJECT_VIEW', 'PROJECT_MANAGE', FEATURE.PROJECTS_BASIC), ['/projects/resources'], { feature: FEATURE.PROJECTS_BASIC }),
  master('projects', 'automation', 'Project Automation', 'Project automation rules and workflow configuration.', 'projectsAutomation', access('PROJECT_VIEW', 'PROJECT_AUTOMATION_MANAGE', FEATURE.PROJECTS_AUTOMATION), ['/projects/automation'], { feature: FEATURE.PROJECTS_AUTOMATION }),

  master('help-desk', 'desk-setup', 'Desk Setup', 'Ticket statuses, priorities, categories and desk setup.', 'deskSetup', access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC), ['/desk/setup'], { feature: FEATURE.DESK_BASIC }),
  master('help-desk', 'workflows', 'Desk Workflows', 'Help desk workflow designer and automation settings.', 'deskWorkflow', access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC), ['/desk/setup/workflows'], { feature: FEATURE.DESK_BASIC }),
  master('help-desk', 'canned-responses', 'Canned Responses', 'Reusable support responses and reply snippets.', 'deskCannedResponses', access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC), ['/desk/canned-responses'], { feature: FEATURE.DESK_BASIC }),
  master('help-desk', 'knowledge-base', 'Knowledge Base', 'Knowledge base categories and article lifecycle.', 'deskKnowledgeBase', access('DESK_VIEW', 'DESK_KB_MANAGE', FEATURE.DESK_BASIC), ['/desk/knowledgebase'], { feature: FEATURE.DESK_BASIC }),
  master('help-desk', 'boards', 'Desk Boards', 'Desk board configuration for project-backed support.', 'deskBoards', access('DESK_VIEW', 'DESK_PROJECT_MANAGE', FEATURE.DESK_PROJECTS), ['/desk/boards'], { feature: FEATURE.DESK_PROJECTS }),

  master('communication', 'social-connect', 'Connection Center', 'Social channel connection and account setup.', 'socialConnect', access('SOCIAL_VIEW', 'SOCIAL_CHANNEL_MANAGE', FEATURE.SOCIAL_BASIC), ['/social/connect'], { feature: FEATURE.SOCIAL_BASIC }),
  master('communication', 'social-settings', 'Social Settings', 'Social media administration and communication settings.', 'socialSettings', access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC), ['/social/settings'], { feature: FEATURE.SOCIAL_BASIC }),
  master('communication', 'social-webhooks', 'Social Webhooks', 'Social webhook configuration and delivery settings.', 'socialWebhooks', access('SOCIAL_VIEW', 'SOCIAL_MANAGE', FEATURE.SOCIAL_BASIC), ['/social/webhooks'], { feature: FEATURE.SOCIAL_BASIC }),

  master('workflow', 'finance-workflows', 'Finance Workflow Builder', 'Finance workflow designer and workflow rules.', 'financeWorkflows', access('FINANCE_VIEW', 'FINANCE_SETTINGS_MANAGE', FEATURE.FINANCE_ACCOUNTING), ['/finance/workflows', '/finance/workflows/designer'], { feature: FEATURE.FINANCE_ACCOUNTING, moduleKey: 'FINANCE' }),
  master('workflow', 'desk-workflows', 'Desk Workflow Builder', 'Desk workflow designer and ticket automation.', 'deskWorkflow', access('DESK_VIEW', 'DESK_WORKFLOW_MANAGE', FEATURE.DESK_BASIC), ['/desk/setup/workflows'], { feature: FEATURE.DESK_BASIC, moduleKey: 'DESK' }),
  master('workflow', 'project-automation', 'Project Workflow Automation', 'Project automation and workflow rules.', 'projectsAutomation', access('PROJECT_VIEW', 'PROJECT_AUTOMATION_MANAGE', FEATURE.PROJECTS_AUTOMATION), ['/projects/automation'], { feature: FEATURE.PROJECTS_AUTOMATION, moduleKey: 'PROJECTS' }),

  master('approvals', 'approval-builder', 'Approval Builder', 'Reusable approval builder for enterprise approval flows.', 'approvalsBuilder', access(['APPROVAL_VIEW', 'APPROVAL_MANAGE'], 'APPROVAL_MANAGE'), ['/pos/approvals/builder']),
  master('approvals', 'approval-matrix', 'Approval Matrix', 'Approval matrix and routing configuration.', 'approvalsMatrix', access(['APPROVAL_VIEW', 'APPROVAL_MATRIX_VIEW'], 'APPROVAL_MANAGE'), ['/pos/approvals/matrix']),
  master('approvals', 'delegations', 'Approval Delegations', 'Delegation rules for approval coverage.', 'approvalDelegations', access(['APPROVAL_DELEGATE', 'APPROVAL_ACT'], 'APPROVAL_DELEGATE'), ['/pos/approvals/delegations']),

  master('notifications', 'notification-center', 'Notification Center', 'Workspace notifications and delivery tracking.', 'notificationsCenter', resourceAccess(null, { create: 'NOTIFICATION_MANAGE', edit: 'NOTIFICATION_MANAGE', delete: 'NOTIFICATION_MANAGE' }), ['/pos/notifications']),

  master('developer', 'api-keys', 'API Keys', 'Developer API keys and integration credentials.', 'genericMasterPlaceholder', access(['ORG_MANAGE']), ['/pos/organization']),
  master('developer', 'lead-webhooks', 'Lead Webhooks', 'Ad and lead source webhooks.', 'leadGenSources', access('LEAD_GEN_VIEW', 'LEAD_GEN_MANAGE'), ['/lead-gen/webhooks'], { moduleKey: 'LEAD_GEN' }),
  master('developer', 'marketing-webhooks', 'Marketing Webhooks', 'Marketing ESP webhooks.', 'marketingWebhooks', access('MARKETING_VIEW', 'MARKETING_MANAGE', FEATURE.MARKETING_EMAIL), ['/marketing/webhooks'], { feature: FEATURE.MARKETING_EMAIL, moduleKey: 'MARKETING' }),
];

export const SETTINGS_CATEGORY_BY_KEY = new Map(SETTINGS_CATEGORIES.map((category) => [category.key, category]));
export const SETTINGS_MASTER_BY_ROUTE = new Map(SETTINGS_MASTERS.map((masterItem) => [masterItem.route, masterItem]));
export const SETTINGS_MASTER_BY_KEY = new Map(SETTINGS_MASTERS.map((masterItem) => [masterItem.key, masterItem]));
export const SETTINGS_LEGACY_ROUTE_SET = new Set(
  SETTINGS_MASTERS.flatMap((masterItem) => masterItem.legacyRoutes.map((route) => route.split('?')[0])),
);

/** Legacy module administration landing URLs — redirect to Settings (workspace home removed). */
export const MODULE_ADMINISTRATION_LEGACY_PATHS = [
  ...moduleScopedAdminLegacyPaths('administration'),
  '/org/administration',
  '/finance/administration',
] as const;

const MODULE_ADMINISTRATION_LEGACY_SET = new Set<string>(MODULE_ADMINISTRATION_LEGACY_PATHS);

export function isModuleAdministrationLegacyPath(pathname: string): boolean {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  return MODULE_ADMINISTRATION_LEGACY_SET.has(path);
}

const SETTINGS_NAV_SUPPLEMENTAL_ROUTES = [
  '/finance/setup/wizard',
  '/procurement/setup',
  ...MODULE_ADMINISTRATION_LEGACY_PATHS,
  ...moduleScopedAdminLegacyPaths('organization', 'companies', 'users', 'roles', 'subscription'),
  ...LAUNCHER_ERP_MODULES.flatMap((mod) => moduleAdminRoutes(mod)),
  ...LAUNCHER_ERP_MODULES.flatMap((mod) =>
    mod === 'HR' ? [] : [`/${moduleSlug(mod)}/users`],
  ),
] as const;

/** Master/config URLs hidden from module sidebars (legacy paths + extras). */
const SETTINGS_NAV_HIDDEN_ROUTES = new Set<string>([
  ...SETTINGS_LEGACY_ROUTE_SET,
  ...SETTINGS_NAV_SUPPLEMENTAL_ROUTES,
]);

export function getSettingsCategory(key: string | undefined): SettingsCategory | undefined {
  if (!key) return undefined;
  return SETTINGS_CATEGORY_BY_KEY.get(key as SettingsCategoryKey);
}

export function getSettingsMastersByCategory(category: SettingsCategoryKey): SettingsMaster[] {
  return SETTINGS_MASTERS.filter((masterItem) => masterItem.category === category);
}

export function getSettingsMasterByPath(pathname: string): SettingsMaster | undefined {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || SETTINGS_PATH;
  return SETTINGS_MASTER_BY_ROUTE.get(path);
}

export function isSettingsLegacyRoute(pathname: string): boolean {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  return SETTINGS_NAV_HIDDEN_ROUTES.has(path);
}

/** Legacy URLs from removed settings categories (Security, System). */
const SETTINGS_REMOVED_CATEGORY_LEGACY: Record<string, string> = {
  '/settings/security/password-policies': '/master/settings',
  '/settings/system': `${SETTINGS_PATH}#organization`,
  '/settings/system/password-policies': '/master/settings',
  '/settings/system/procurement-setup': '/procurement/setup',
  '/settings/system/feature-flags': `${SETTINGS_PATH}#organization`,
  '/settings/localization': `${SETTINGS_PATH}#organization`,
  '/settings/localization/currencies': '/finance/setup',
  '/settings/localization/countries-timezones': '/settings/organization/organization-profile',
  '/settings/localization/display-formats': '/settings/organization/organization-profile',
  '/settings/hr/hr-master-data': `${SETTINGS_PATH}#hr-master-data`,
  '/settings/hr/holidays': `${SETTINGS_PATH}#hr-master-data`,
  '/settings/hr-master-data/shift-schedules': '/hr/shifts',
  '/settings/hr/shifts': '/hr/shifts',
};

/** Map a legacy master URL to its canonical `/settings/{category}/{master}` route. */
export function getSettingsRouteForLegacyPath(pathname: string, search = ''): string | undefined {
  const [path, embeddedQuery] = pathname.split('?');
  const normalized = path.replace(/\/+$/, '') || '/';
  const queryString = search || (embeddedQuery ? `?${embeddedQuery}` : '');

  const hrMasterDataTarget = resolveHrMasterDataLegacySettingsPath(normalized, queryString);
  if (hrMasterDataTarget) return hrMasterDataTarget;

  if (isModuleAdministrationLegacyPath(normalized)) {
    return queryString ? `${SETTINGS_PATH}${queryString}` : SETTINGS_PATH;
  }
  if (normalized === '/settings/security') {
    return `${SETTINGS_PATH}#organization`;
  }
  const removedCategoryTarget = SETTINGS_REMOVED_CATEGORY_LEGACY[normalized];
  if (removedCategoryTarget) {
    return queryString ? `${removedCategoryTarget}${queryString}` : removedCategoryTarget;
  }
  for (const masterItem of SETTINGS_MASTERS) {
    if (masterItem.legacyRoutes.some((legacy) => legacy.split('?')[0].replace(/\/+$/, '') === normalized)) {
      return queryString ? `${masterItem.route}${queryString}` : masterItem.route;
    }
  }
  return undefined;
}

export function resolveSettingsMasterModuleKey(master: SettingsMaster): SettingsModuleKey {
  return master.moduleKey ?? SETTINGS_CATEGORY_MODULE[master.category] ?? 'PLATFORM';
}

/** Organization and other platform masters — visible in every module Settings. */
export function isSharedSettingsMaster(master: SettingsMaster): boolean {
  return resolveSettingsMasterModuleKey(master) === 'PLATFORM';
}

export function isSettingsMasterVisibleInModule(
  master: SettingsMaster,
  scopeModule: SettingsModuleKey,
): boolean {
  const masterModule = resolveSettingsMasterModuleKey(master);
  if (masterModule === 'PLATFORM') return true;
  return masterModule === scopeModule;
}

function resolveSettingsCategoryModuleKey(categoryKey: SettingsCategoryKey): SettingsModuleKey {
  const moduleKeys = new Set(
    getSettingsMastersByCategory(categoryKey).map((master) => resolveSettingsMasterModuleKey(master)),
  );
  if (moduleKeys.size === 1) {
    return [...moduleKeys][0] ?? SETTINGS_CATEGORY_MODULE[categoryKey] ?? 'PLATFORM';
  }
  if (moduleKeys.size > 1) return 'PLATFORM';
  return SETTINGS_CATEGORY_MODULE[categoryKey] ?? 'PLATFORM';
}

/** ERP module bucket for a settings URL (used by role screen matrix and route registry). */
export function resolveSettingsModuleKey(pathname: string): SettingsModuleKey {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || SETTINGS_PATH;
  if (path === SETTINGS_PATH) return 'PLATFORM';

  const parts = path.split('/').filter(Boolean);
  if (parts[0] !== 'settings') return 'PLATFORM';

  const categoryKey = parts[1] as SettingsCategoryKey | undefined;
  if (!categoryKey) return 'PLATFORM';

  if (parts.length === 2) {
    return resolveSettingsCategoryModuleKey(categoryKey);
  }

  const master = getSettingsMasterByPath(path);
  if (master) return resolveSettingsMasterModuleKey(master);

  return SETTINGS_CATEGORY_MODULE[categoryKey] ?? 'PLATFORM';
}

export function isSettingsModuleEnabled(
  moduleKey: SettingsModuleKey,
  enabledModules: ErpModule[],
): boolean {
  if (moduleKey === 'PLATFORM') return true;
  return enabledModules.includes(moduleKey);
}

export type SettingsModuleGroup = {
  moduleKey: SettingsModuleKey;
  label: string;
  categories: SettingsCategory[];
};

export function groupSettingsCategoriesByModule(
  categories: SettingsCategory[],
): SettingsModuleGroup[] {
  const buckets = new Map<SettingsModuleKey, SettingsCategory[]>();
  for (const category of categories) {
    const moduleKey = resolveSettingsCategoryModuleKey(category.key);
    const list = buckets.get(moduleKey) ?? [];
    list.push(category);
    buckets.set(moduleKey, list);
  }

  return SETTINGS_MODULE_GROUP_ORDER.filter((key) => buckets.has(key)).map((moduleKey) => ({
    moduleKey,
    label: SETTINGS_MODULE_GROUP_LABELS[moduleKey] ?? moduleKey,
    categories: (buckets.get(moduleKey) ?? []).sort((a, b) => a.order - b.order),
  }));
}

export function getSettingsRoutesAccess(): Record<string, RouteAccessRule> {
  const routes: Record<string, RouteAccessRule> = {
    [SETTINGS_PATH]: access(
      [
        'ORG_VIEW',
        'ORG_MANAGE',
        'ROLE_VIEW',
        'HR_VIEW',
        'FINANCE_VIEW',
        'PRODUCT_VIEW',
        'CRM_VIEW',
        'DESK_VIEW',
        'MARKETING_VIEW',
        'PROJECT_VIEW',
        'WAREHOUSE_VIEW',
      ],
      'ORG_MANAGE',
    ),
  };

  for (const category of SETTINGS_CATEGORIES) {
    const masters = getSettingsMastersByCategory(category.key);
    const hasOpenView = masters.some((item) => item.access.view == null);
    routes[`${SETTINGS_PATH}/${category.key}`] = {
      view: hasOpenView
        ? null
        : masters.flatMap((item) => {
            const view = item.access.view;
            if (Array.isArray(view)) return view;
            return view ? [view] : [];
          }),
    };
  }

  for (const masterItem of SETTINGS_MASTERS) {
    routes[masterItem.route] = masterItem.access;
  }

  return routes;
}

export function searchSettings(query: string): SettingsMaster[] {
  const q = query.trim().toLowerCase();
  if (!q) return SETTINGS_MASTERS;
  return SETTINGS_MASTERS.filter((item) => {
    const category = SETTINGS_CATEGORY_BY_KEY.get(item.category);
    return [
      item.label,
      item.description,
      category?.label,
      ...(item.tags ?? []),
    ]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(q));
  });
}
